import { useSession } from 'next-auth/react'

import type { KeyedMutator } from 'swr'
import useSWR from 'swr'

import useRechargeSession from '@lib/hooks/useRechargeSession'
import {
	getLastCharge,
	getRechargeAddress,
	getSubscriptionOrders,
	getSubscriptions,
	loginWithShopify
} from '@lib/operations/recharge'
import type { SubscriptioBoxContractDetails } from '@lib/types/profile-pages'
import { formatDateString } from '@lib/utils/dates'

type UseSubscriptionBoxContractDetailsReturn = {
	subscriptioBoxContractDetails: SubscriptioBoxContractDetails | null | undefined
	loading: boolean
	revalidate: KeyedMutator<SubscriptioBoxContractDetails | null>
}

export default function useSubscriptionBoxContractDetails(): UseSubscriptionBoxContractDetailsReturn {
	const { data: session } = useSession()

	const accessToken = (session as any)?.shopifyAccessToken as string | undefined
	const swrKey = accessToken ? `/subscription-box-contract-details/${accessToken}` : null

	const { loading: sessionLoading } = useRechargeSession()

	const { data, isValidating, mutate, error } = useSWR(swrKey, getSubscriptionBoxContractDetails, {
		onErrorRetry: (err, key, config, revalidate, { retryCount }) => {
			if (retryCount >= 2) return
			setTimeout(() => revalidate({ retryCount }), 5000)
		}
	})

	async function getSubscriptionBoxContractDetails() {
		const rechargeSession = await loginWithShopify(accessToken)
		if (rechargeSession) {
			// returning first subscription contract in array
			const rechargeSubscriptions = await getSubscriptions(rechargeSession)
			const subscriptionBoxContractDetails = rechargeSubscriptions ? rechargeSubscriptions[0] : null

			if (subscriptionBoxContractDetails) {
				const lastCharge = await getLastCharge(rechargeSession, subscriptionBoxContractDetails.id)
				const processedAtDate = lastCharge?.processed_at ? new Date(lastCharge.processed_at) : null
				if (processedAtDate) processedAtDate.setDate(processedAtDate.getDate() - 1)
				const subscriptionOrdersAfterCharge = processedAtDate
					? await getSubscriptionOrders(
							rechargeSession,
							subscriptionBoxContractDetails.id,
							processedAtDate.toISOString(),
							'success'
						)
					: []

				// const afterChargeOrders = subscriptionOrders?.filter()
				const subscriptionAddress = await getRechargeAddress(
					rechargeSession,
					subscriptionBoxContractDetails.address_id
				)
				return {
					autoRenewStatus:
						subscriptionBoxContractDetails.status === 'active' ? 'ENABLED' : 'CONTRACT_CANCELED',
					subscriptionContractId: subscriptionBoxContractDetails.id.toString(),
					// TODO: pending to fill payment orders with this data
					boxesShipped: subscriptionOrdersAfterCharge?.length, // subscriptionBoxContractDetails?.boxesShipped,
					planTotalBoxes: subscriptionBoxContractDetails.charge_interval_frequency,
					// TODO: can we delete this field?
					subscriptionType: 'RECURRING',
					sellingPlanName: subscriptionBoxContractDetails.product_title,
					nextBillingDate: formatDateString(
						subscriptionBoxContractDetails.next_charge_scheduled_at || ''
					) as string,
					// TODO: can we delete this field?
					isSubscriptionGift: false,
					shippingAddress: subscriptionAddress!
				} as SubscriptioBoxContractDetails
			}
		}

		return null
	}

	return {
		subscriptioBoxContractDetails: data,
		loading: isValidating && !error,
		revalidate: mutate
	}
}
